import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, ArrowLeft, Check, Crown, Eye, EyeOff } from "lucide-react";
import { useProState } from "../lib/pro";
import UpgradeButton from "../components/UpgradeButton";
import { toast } from "sonner";

const LOGO_SRC = "/popupprofit-logo.png";

const FREE_INCLUDES = [
  "Profit calculator for any event",
  "1 saved event in the planner",
  "Booth-day checklist",
  "No credit card required",
];

export default function Signup() {
  const { register } = useProState();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) { setError("Please enter a valid email"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await register(trimmed, password);
      toast.success("Welcome to PopUp Profit");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Could not create your account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="signup-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute hover:text-brand-green mb-6" data-testid="signup-back">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Form (left/main) */}
        <div className="md:col-span-3">
          <div className="pop-card p-7 sm:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-grid place-items-center w-11 h-11 rounded-2xl bg-white border border-brand-line overflow-hidden">
                <img src={LOGO_SRC} alt="PopUp Profit logo" className="w-9 h-9 object-contain" />
              </span>
              <p className="label-eyebrow">Create your free account</p>
            </div>

            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-brand-ink mt-4">
              Start planning your next pop-up — free.
            </h1>
            <p className="text-sm text-brand-mute mt-2">
              Free forever to start. Upgrade to Pro any time for unlimited events, product tracking, and post-event reports.
            </p>

            <form onSubmit={onSubmit} className="mt-6" data-testid="signup-form" noValidate>
              <div>
                <label className="pop-label" htmlFor="signup-email">Email</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
                  <input
                    id="signup-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@vendor.shop"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pop-input pl-10"
                    data-testid="signup-email-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="pop-label" htmlFor="signup-password">Password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
                  <input
                    id="signup-password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pop-input pl-10 pr-10"
                    data-testid="signup-password-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-mute hover:text-brand-ink"
                    tabIndex={-1}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <label className="pop-label" htmlFor="signup-confirm">Confirm password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
                  <input
                    id="signup-confirm"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pop-input pl-10"
                    data-testid="signup-confirm-input"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <p className="mt-3 text-sm text-brand-error" data-testid="signup-error">{error}</p>
              )}

              <button
                type="submit"
                className="pop-btn-primary w-full mt-5 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="signup-submit"
                disabled={loading}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating your account…</>
                ) : (
                  <>Create Account</>
                )}
              </button>

              <p className="text-[11px] text-brand-mute mt-3 text-center">
                By creating an account, you agree to use PopUp Profit for vendor planning. No credit card required.
              </p>
            </form>

            <p className="text-xs text-brand-mute mt-6 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-green font-medium hover:underline" data-testid="signup-to-login">
                Sign in →
              </Link>
            </p>
          </div>
        </div>

        {/* Free perks + Pro upsell (right/sidebar) */}
        <aside className="md:col-span-2 space-y-4">
          <div className="pop-card p-5">
            <p className="label-eyebrow">What you get</p>
            <ul className="mt-3 space-y-2.5">
              {FREE_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-brand-ink">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-green/10 text-brand-green grid place-items-center shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="pop-card p-5 bg-brand-cream-soft border-brand-line">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-gold/15 text-brand-gold grid place-items-center shrink-0">
                <Crown className="w-5 h-5" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-sm font-semibold text-brand-ink">Want everything?</p>
                <p className="text-xs text-brand-mute mt-1">
                  Pro adds unlimited events, post-event reports, product library, and printable checklists — $9/month, cancel anytime.
                </p>
                <div className="mt-3">
                  <UpgradeButton variant="ghost" label="See Pro features" testid="signup-upgrade-btn" />
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
